// services/aiFundingApi.js
import { API_URL } from "../../../config";

async function safeJson(res) {
  try { return await res.json(); } catch { return {}; }
}

export async function classifyIndustryApi(description) {
  const res = await fetch(`${API_URL}/ai/classify-industry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ description }),
  });

  const data = await safeJson(res);

  if (res.status === 403) return { ok: false, code: "FORBIDDEN", message: data?.message || "Access denied" };
  if (res.status === 429) return { ok: false, code: "RATE_LIMIT", message: data?.message || "Daily classification limit reached" };
  if (res.status === 400) return { ok: false, code: "BAD_REQUEST", message: data?.message || "Bad request" };
  if (!res.ok) return { ok: false, code: "SERVER_ERROR", message: data?.message || "Server error" };

  if (data?.status === "matched" && data?.industry) return { ok: true, status: "matched", industry: data.industry };
  if (data?.status === "not_matched") return { ok: true, status: "not_matched", message: data?.message };

  return { ok: false, code: "INVALID_RESPONSE", message: "Unexpected response format from the server" };
}

export async function runFundingAnalysisApi({ description, minAmount = null, maxAmount = null }) {
  const payload = { description };
  if (minAmount != null) payload.amount_min = minAmount;
  if (maxAmount != null) payload.amount_max = maxAmount;

  const res = await fetch(`${API_URL}/ai/funding-analysis`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await safeJson(res);

  if (res.status === 403) return { ok: false, code: "FORBIDDEN", message: data?.message || "Access denied" };
  if (res.status === 429) return { ok: false, code: "RATE_LIMIT", message: data?.message || "Daily analysis limit reached" };
  if (res.status === 400) return { ok: false, code: "BAD_REQUEST", message: data?.message || "Bad request" };
  if (!res.ok) return { ok: false, code: "SERVER_ERROR", message: data?.message || "Server error" };

  if (data?.status === "ok") return { ok: true, analysis: data };
  if (data?.status === "not_matched") return { ok: false, code: "NO_MATCH", message: data?.message || "No matching industry found", analysis: data };

  return { ok: false, code: "INVALID_RESPONSE", message: "Unexpected response format from the server" };
}

export async function fetchFundingHistoryApi({ limit = 20, offset = 0 } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const res = await fetch(`${API_URL}/ai/funding-history?${params.toString()}`, {
    method: "GET",
    credentials: "include",
  });

  const data = await safeJson(res);
  if (res.status === 403) return { ok: false, code: "FORBIDDEN", message: data?.message || "Access denied" };
  if (!res.ok) return { ok: false, code: "SERVER_ERROR", message: data?.message || "Server error" };
  if (data?.status === "ok" && Array.isArray(data?.items)) {
    return {
      ok: true,
      items: data.items,
      total: data.total ?? data.items.length,
      limit: data.limit ?? limit,
      offset: data.offset ?? offset,
    };
  }

  return { ok: false, code: "INVALID_RESPONSE", message: "Unexpected response format from the server" };
}

export async function fetchFundingHistoryDetailApi(requestId) {
  const res = await fetch(`${API_URL}/ai/funding-history/${encodeURIComponent(requestId)}`, {
    method: "GET",
    credentials: "include",
  });

  const data = await safeJson(res);
  if (res.status === 403) return { ok: false, code: "FORBIDDEN", message: data?.message || "Access denied" };
  if (res.status === 404) return { ok: false, code: "NOT_FOUND", message: data?.message || "History item not found" };
  if (!res.ok) return { ok: false, code: "SERVER_ERROR", message: data?.message || "Server error" };
  if (data?.status === "ok" && data?.analysis) return { ok: true, item: data.item, analysis: data.analysis };

  return { ok: false, code: "INVALID_RESPONSE", message: "Unexpected response format from the server" };
}

export async function fetchIndustryDealsApi(industry_category) {
  const res = await fetch(`${API_URL}/ai/industry-deals`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ industry_category }),
  });

  const data = await safeJson(res);

  if (res.status === 403) return { ok: false, code: "FORBIDDEN", message: data?.message || "Access denied" };
  if (res.status === 429) return { ok: false, code: "RATE_LIMIT", message: data?.message || "Rate limited" };
  if (res.status === 400) return { ok: false, code: "BAD_REQUEST", message: data?.message || "Bad request" };
  if (!res.ok) return { ok: false, code: "SERVER_ERROR", message: data?.message || "Server error" };

  // Expect { status:'ok', industry_category, total, latest:[...] }
  if (data?.status === "ok" && Array.isArray(data?.latest)) {
    return { ok: true, total: data.total ?? 0, latest: data.latest, industry_category: data.industry_category };
  }
  return { ok: false, code: "INVALID_RESPONSE", message: "Unexpected response format from server" };
}

export async function fetchIndustryDealsWithAmountApi(industry_category, { minAmount = null, maxAmount = null } = {}) {
  const payload = { industry_category };
  if (minAmount != null) payload.amount_min = minAmount;
  if (maxAmount != null) payload.amount_max = maxAmount;
  const res = await fetch(`${API_URL}/ai/industry-deals`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await safeJson(res);

  if (res.status === 403) return { ok: false, code: "FORBIDDEN", message: data?.message || "Access denied" };
  if (res.status === 429) return { ok: false, code: "RATE_LIMIT", message: data?.message || "Rate limited" };
  if (res.status === 400) return { ok: false, code: "BAD_REQUEST", message: data?.message || "Bad request" };
  if (!res.ok) return { ok: false, code: "SERVER_ERROR", message: data?.message || "Server error" };

  if (data?.status === "ok" && Array.isArray(data?.latest)) {
    return { ok: true, total: data.total ?? 0, latest: data.latest, industry_category: data.industry_category };
  }
  return { ok: false, code: "INVALID_RESPONSE", message: "Unexpected response format from server" };
}

export async function fetchIndustryInvestorsApi(industry_category, { minAmount = null, maxAmount = null, dealIds = [], debug = false } = {}) {
  const payload = { industry_category };
  if (minAmount != null) payload.amount_min = minAmount;
  if (maxAmount != null) payload.amount_max = maxAmount;
  if (Array.isArray(dealIds) && dealIds.length) payload.deal_ids = dealIds;
  if (debug) payload.debug = true;
  const res = await fetch(`${API_URL}/ai/industry-investors`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = await safeJson(res);
  if (res.status === 403) return { ok: false, code: "FORBIDDEN", message: data?.message || "Access denied" };
  if (res.status === 429) return { ok: false, code: "RATE_LIMIT", message: data?.message || "Rate limited" };
  if (res.status === 400) return { ok: false, code: "BAD_REQUEST", message: data?.message || "Bad request" };
  if (!res.ok) return { ok: false, code: "SERVER_ERROR", message: data?.message || "Server error" };

  if (data?.status === "ok" && Array.isArray(data?.investors)) {
    return { ok: true, investors: data.investors, total: data.total ?? 0, debug: data.debug };
  }
  return { ok: false, code: "INVALID_RESPONSE", message: "Unexpected response format from server" };
}
